import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Ac, BaseQueryParams, IResponse, IResponsePaginate, AcDto, WithMetadata, SettingsAc, AcHistory } from '@/types';
import { from, Observable, switchMap } from 'rxjs';
import { AuthorizationService } from '@/services/authorization.service';
import { form } from '@/utils/form.utils';

interface CommitteeQuery extends BaseQueryParams {
  nom?: string;
  ancrage?: string;
  contact?: string;
}

interface SeuilDto {
  acId: number;
  typeMarcherId: number;
  modePassationId: number;
  seuil: number;
}

interface Seuil {
  id: {
    ac: any;
    typeMarcher: any;
    modePassation: any;
  };
  seuil: number;
}

interface ModePassation {
  id: number;
  libelle: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContractingAuthorityService {
  authService = inject(AuthorizationService);

  BASEURL = `${environment.api.paramsUrl}/ac`;
  AC_HISTORICAL_URL = `${environment.api.paramsUrl}/user-ac-historique`;
  httpClient = inject(HttpClient);

  getContractingAuthorities(params?: CommitteeQuery): Observable<IResponsePaginate<Ac>> {
    return this.httpClient.get<IResponsePaginate<Ac>>(`${this.BASEURL}`, { params: params as HttpParams });
  }
  getContractingAuthoritiesList(params?: CommitteeQuery): Observable<IResponse<Ac>> {
    return this.httpClient.get<IResponse<Ac>>(`${this.BASEURL}/list`, { params: params as HttpParams });
  }
  getContractingAuthoritiesListUm(params?: CommitteeQuery): Observable<IResponse<Ac>> {
    return this.httpClient.get<IResponse<Ac>>(`${this.BASEURL}/list-um`, { params: params as HttpParams });
  }
  getOneContractingAuthorityBatch(id: number): Observable<WithMetadata<SettingsAc>> {
    return this.httpClient.get<WithMetadata<SettingsAc>>(`${this.BASEURL}/batch/${id}`);
  }

  addContractingAuthority(Ac: AcDto | FormData): Observable<WithMetadata<SettingsAc>> {
    if (Ac instanceof FormData) {
      return this.httpClient.post<WithMetadata<SettingsAc>>(`${this.BASEURL}`, Ac);
    } else {
      const formData = form.transformObjectToFormData(Ac);
      return this.httpClient.post<WithMetadata<SettingsAc>>(`${this.BASEURL}`, formData);
    }
  }

  updateContractingAuthority(id: number, Ac: AcDto | FormData): Observable<IResponse<Ac>> {
    if (Ac instanceof FormData) {
      return this.httpClient.put<IResponse<Ac>>(`${this.BASEURL}/${id}`, Ac);
    } else {
      const formData = form.transformObjectToFormData(Ac);
      return this.httpClient.put<IResponse<Ac>>(`${this.BASEURL}/${id}`, formData);
    }
  }

  deleteContractingAuthority(id: number): Observable<IResponse<Ac>> {
    return this.httpClient.delete<IResponse<Ac>>(`${this.BASEURL}/${id}`);
  }

  getContractingAuthoritiesByUser(userId?: string): Observable<Ac[]> {
    return this.httpClient.get<Ac[]>(`${this.BASEURL}/user/${userId}/acs`);
  }

  getCurrentUserContractingAuthority(): Observable<Ac[]> {
    return from(this.authService.getUserId()).pipe(switchMap(userId => this.getContractingAuthoritiesByUser(userId)));
  }

  getComs(params?: any): Observable<IResponsePaginate<any>> {
    return this.httpClient.get<IResponsePaginate<any>>(`${environment.api.paramsUrl}/com`, { params: params as HttpParams });
  }

  getTutels(): Observable<Ac[]> {
    return this.httpClient.get<IResponse<Ac>>(`${this.BASEURL}/is_tutel`);
  }

  getModePassations(): Observable<IResponsePaginate<ModePassation>> {
    return this.httpClient.get<IResponsePaginate<ModePassation>>(`${environment.api.paramsUrl}/mode_passation`);
  }

  // Commission methods
  getCommissionsByTutel(acId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.api.paramsUrl}/com/by_own_tutel/${acId}`);
  }

  getPrivateCommissions(acId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.api.paramsUrl}/com/private_com_of_ac/${acId}`);
  }

  getAllCommissions(acId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.api.paramsUrl}/com/all_coms_of_tutels/${acId}`);
  }

  getAllCommissionsForAdd(): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.api.paramsUrl}/com`);
  }

  getAcPhoto(acId: number): Observable<{ content: string; type: string }> {
    return this.httpClient.get<{ content: string; type: string }>(`${this.BASEURL}/${acId}/photo/display`);
  }

  getAcHistory(acId: number, date: string): Observable<IResponsePaginate<AcHistory>> {
    return this.httpClient.get<IResponsePaginate<AcHistory>>(`${this.AC_HISTORICAL_URL}/${acId}`, { params: { date: date } });
  }
}
